import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const MAX_LOG_LINES = 2000;

export function useSocket(token) {
  const socketRef    = useRef(null);
  const tokenRef     = useRef(token);
  const [logs, setLogs]     = useState([]);
  const [status, setStatus] = useState("disconnected");
  const [error, setError]   = useState(null);
  const currentFileRef = useRef(null);

  useEffect(() => { tokenRef.current = token; }, [token]);

  useEffect(() => {
    if (!token) return;

    setStatus("connecting");
    const socket = io(BACKEND_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socket.on("connect", () => {
      setStatus("connected");
      setError(null);
      if (currentFileRef.current) {
        socket.emit("subscribe:file", currentFileRef.current);
      }
    });

    socket.on("disconnect", (reason) => {
      setStatus("disconnected");
      if (reason === "io server disconnect") socket.connect();
    });

    socket.on("connect_error", (err) => {
      setStatus("error");
      setError(`Connection error: ${err.message}`);
    });

    socket.on("log:data", ({ line, ts }) => {
      setLogs((prev) => {
        const next = [...prev, { line, ts, id: `live-${ts}-${Math.random()}` }];
        return next.length > MAX_LOG_LINES ? next.slice(-MAX_LOG_LINES) : next;
      });
    });

    socket.on("log:subscribed", () => {
      setError(null);
    });

    socket.on("log:error", (msg) => setError(msg));

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
      setStatus("disconnected");
    };
  }, [token]);

  const subscribe = useCallback(async (filePath) => {
    currentFileRef.current = filePath;
    setLogs([]);
    setError(null);

    // Step 1: Load last 100 lines via REST
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/logs/tail?file=${encodeURIComponent(filePath)}&lines=100`,
        { headers: { Authorization: `Bearer ${tokenRef.current}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const historyLogs = (data.lines || []).map((line, i) => ({
          line,
          ts: Date.now() - (data.lines.length - i),
          id: `history-${i}-${Date.now()}`,
          isHistory: true,
        }));
        setLogs(historyLogs);
      }
    } catch (e) {
      // Non-fatal — still continue to live stream
    }

    // Step 2: Start live stream
    if (socketRef.current?.connected) {
      socketRef.current.emit("subscribe:file", filePath);
    }
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  return { logs, status, error, subscribe, clearLogs };
}
